import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Counted } from "../../stock/model";
import { fmt, shopColor, shortDate, STATUS, weekNumber, dayDate } from "./look";
import type { Status } from "../../stock/model";

// Small hand-drawn charts for the Inventory tool: thin marks, a recessive grid, the numbers in ink (never in the
// series colour), a tooltip on every mark, and a legend wherever there is more than one series.

const INK_FAINT = "#9c888e";
const GRID = "#f3e6e9";
const LINE = "#c8546a";

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(320);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver(([entry]) => entry && setWidth(Math.max(200, Math.floor(entry.contentRect.width))));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

function Tip({ x, y, width, children }: { x: number; y: number; width: number; children: ReactNode }) {
  // Kept inside the chart: to the left of the pointer once past the middle.
  const left = x > width / 2;
  return (
    <div
      className="pointer-events-none absolute z-10 min-w-32 rounded-xl border border-line-soft bg-white px-3 py-2 text-[12px] leading-snug text-ink shadow-md"
      style={{ top: Math.max(0, y - 8), ...(left ? { right: width - x + 12 } : { left: x + 12 }) }}
    >
      {children}
    </div>
  );
}

/** Twelve weeks in a table cell: one thin bar a week, the latest darker. */
export function MiniBars({ values, labels, max }: { values: number[]; labels: string[]; max: number }) {
  const w = 4;
  const gap = 2;
  const h = 22;
  const top = Math.max(1, max);
  return (
    <svg width={values.length * (w + gap) - gap} height={h} className="block" aria-hidden="true">
      {values.map((v, i) => {
        const bh = v > 0 ? Math.max(2, (v / top) * h) : 1;
        return (
          <rect key={i} x={i * (w + gap)} y={h - bh} width={w} height={bh} rx={v > 0 ? 1.5 : 0} fill={v > 0 ? (i === values.length - 1 ? LINE : "#e3b3bd") : GRID}>
            <title>{`${labels[i]}: ${fmt(v)}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

/** How many weeks the stock lasts, against the lead time (the tick): a bar that reaches past the tick is fine. */
export function LastsBar({ weeks, leadWeeks, status, scale = 26 }: { weeks: number | null; leadWeeks: number | null; status: Status; scale?: number }) {
  if (weeks === null) return <span className="text-ink-faint">–</span>;
  const share = Math.min(1, weeks / scale);
  return (
    <span className="flex items-center gap-2" title={`Lasts about ${fmt(weeks)} weeks${leadWeeks ? `; a delivery takes ${fmt(leadWeeks)} weeks` : ""}`}>
      <span className="relative h-2 w-20 shrink-0 overflow-hidden rounded-full bg-[#f6e9ec]">
        <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${Math.max(4, share * 100)}%`, background: STATUS[status].bar }} />
        {leadWeeks !== null && leadWeeks <= scale && <span className="absolute inset-y-[-2px] w-[2px] bg-ink/60" style={{ left: `${(leadWeeks / scale) * 100}%` }} />}
      </span>
      <span className="tabular-nums">{weeks >= 100 ? "99+" : fmt(weeks)}</span>
    </span>
  );
}

/** The warehouse counts over time, and the estimate for today as a dashed step on from the last count. */
export function CountChart({ counted, nowEstimate, today, red, unit = "boxes" }: { counted: Counted; nowEstimate: number | null; today: string; red: number | null; unit?: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const points = counted.history.map((h) => ({ at: h.at, n: h.n, estimate: false }));
  if (nowEstimate !== null && counted.at && counted.at.slice(0, 10) < today) points.push({ at: `${today}T12:00:00`, n: nowEstimate, estimate: true });
  if (!points.length) return null;
  const height = 150;
  const m = { left: 30, right: 12, top: 12, bottom: 22 };
  const t = (iso: string) => new Date(`${iso.slice(0, 19)}Z`).getTime();
  const x0 = t(points[0]!.at);
  const x1 = Math.max(t(points.at(-1)!.at), x0 + 86_400_000);
  const top = Math.max(...points.map((p) => p.n), red ?? 0, 4) * 1.12;
  const x = (iso: string) => m.left + ((t(iso) - x0) / (x1 - x0)) * (width - m.left - m.right);
  const y = (n: number) => m.top + (1 - n / top) * (height - m.top - m.bottom);
  const steps = [0, Math.round(top / 2), Math.round(top * 0.9)];
  const months: { at: number; label: string }[] = [];
  for (let d = new Date(x0); d.getTime() <= x1; d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1))) {
    const first = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
    if (first >= x0) months.push({ at: first, label: new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: "UTC" }).format(first) });
  }
  const counts = points.filter((p) => !p.estimate);
  const line = counts.map((p, i) => `${i ? "L" : "M"}${x(p.at)},${y(p.n)}`).join("");
  const last = counts.at(-1)!;
  const estimate = points.find((p) => p.estimate);
  const near = (px: number) => points.reduce((best, p, i) => (Math.abs(x(p.at) - px) < Math.abs(x(points[best]!.at) - px) ? i : best), 0);
  const hovered = hover === null ? null : points[hover]!;
  return (
    <div ref={ref} className="relative">
      <svg
        width={width}
        height={height}
        className="block touch-none"
        onPointerMove={(e) => setHover(near(e.clientX - e.currentTarget.getBoundingClientRect().left))}
        onPointerLeave={() => setHover(null)}
        role="img"
        aria-label={`Warehouse counts: ${counts.map((p) => `${shortDate(p.at)} ${p.n}`).join(", ")}`}
      >
        {steps.map((s) => (
          <g key={s}>
            <line x1={m.left} x2={width - m.right} y1={y(s)} y2={y(s)} stroke={GRID} />
            <text x={m.left - 6} y={y(s) + 4} textAnchor="end" fontSize="10" fill={INK_FAINT}>{fmt(s)}</text>
          </g>
        ))}
        {months.map((mo) => (
          <text key={mo.at} x={m.left + ((mo.at - x0) / (x1 - x0)) * (width - m.left - m.right)} y={height - 6} fontSize="10" fill={INK_FAINT} textAnchor="middle">{mo.label}</text>
        ))}
        {red !== null && (
          <g>
            <line x1={m.left} x2={width - m.right} y1={y(red)} y2={y(red)} stroke={STATUS.order.bar} strokeWidth="1" strokeDasharray="4 4" opacity="0.7" />
            <text x={m.left + 4} y={y(red) - 4} fontSize="10" fill="#9b2121" stroke="#fff" strokeWidth="3" paintOrder="stroke">Red level {fmt(red)}</text>
          </g>
        )}
        <path d={line} fill="none" stroke={LINE} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {estimate && <path d={`M${x(last.at)},${y(last.n)}L${x(estimate.at)},${y(estimate.n)}`} stroke={LINE} strokeWidth="2" strokeDasharray="3 4" fill="none" />}
        {hovered && <line x1={x(hovered.at)} x2={x(hovered.at)} y1={m.top} y2={height - m.bottom} stroke="#e8cdd3" />}
        {points.map((p, i) => (
          <circle key={i} cx={x(p.at)} cy={y(p.n)} r={hover === i ? 5 : 4} fill={p.estimate ? "#fff" : LINE} stroke={p.estimate ? LINE : "#fff"} strokeWidth="2" />
        ))}
        <text x={x(last.at)} y={y(last.n) - 10} textAnchor={x(last.at) > width - 40 ? "end" : "middle"} fontSize="11" fontWeight="700" fill="#2c1a20">{fmt(last.n)}</text>
      </svg>
      {hovered && (
        <Tip x={x(hovered.at)} y={y(hovered.n)} width={width}>
          <div className="font-bold">{hovered.estimate ? "Today, estimated" : `Counted ${dayDate(hovered.at)}`}</div>
          <div className="text-ink-soft">
            {fmt(hovered.n)} {unit}
            {hovered.estimate && <span className="block">the last count less what the shops requested since</span>}
          </div>
        </Tip>
      )}
    </div>
  );
}

type WeekBars = { weeks: string[]; shops: string[]; allShops: string[]; value: (shop: string, week: string) => number; note?: (shop: string, week: string) => string[] };

/** What the shops asked for, week by week, stacked by shop. */
export function WeeklyBars({ weeks, shops, allShops, value, note }: WeekBars) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const height = 160;
  const m = { left: 28, right: 6, top: 16, bottom: 22 };
  const totals = weeks.map((w) => shops.reduce((t, s) => t + value(s, w), 0));
  const top = Math.max(...totals, 2) * 1.1;
  const band = (width - m.left - m.right) / weeks.length;
  const bar = Math.min(24, band * 0.68);
  const y = (n: number) => m.top + (1 - n / top) * (height - m.top - m.bottom);
  const steps = [0, Math.round(top / 2), Math.round(top * 0.9)].filter((s, i, all) => all.indexOf(s) === i);
  const hovered = hover === null ? null : weeks[hover]!;
  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-ink-soft">
        {shops.map((s) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-[3px]" style={{ background: shopColor(allShops, s) }} />
            {s}
          </span>
        ))}
      </div>
      <div ref={ref} className="relative">
        <svg width={width} height={height} className="block touch-none" onPointerLeave={() => setHover(null)} role="img" aria-label="Requested per week by the shops">
          {steps.map((s) => (
            <g key={s}>
              <line x1={m.left} x2={width - m.right} y1={y(s)} y2={y(s)} stroke={GRID} />
              <text x={m.left - 6} y={y(s) + 4} textAnchor="end" fontSize="10" fill={INK_FAINT}>{fmt(s)}</text>
            </g>
          ))}
          {weeks.map((w, i) => {
            const cx = m.left + band * i + band / 2;
            let base = 0;
            const parts = shops.map((s) => ({ s, v: value(s, w) })).filter((p) => p.v > 0);
            return (
              <g key={w} opacity={hover === null || hover === i ? 1 : 0.45}>
                {hover === i && <rect x={cx - band / 2} y={m.top} width={band} height={height - m.top - m.bottom} fill="#fbf0f2" />}
                {parts.map((p, k) => {
                  const y1 = y(base + p.v);
                  const y0 = y(base);
                  base += p.v;
                  const h = Math.max(1, y0 - y1 - (k ? 2 : 0));
                  const isTop = k === parts.length - 1;
                  const r = isTop ? Math.min(4, h, bar / 2) : 0;
                  const x0 = cx - bar / 2;
                  return <path key={p.s} fill={shopColor(allShops, p.s)} d={`M${x0},${y1 + h}V${y1 + r}Q${x0},${y1} ${x0 + r},${y1}H${x0 + bar - r}Q${x0 + bar},${y1} ${x0 + bar},${y1 + r}V${y1 + h}Z`} />;
                })}
                {(i % 4 === (weeks.length - 1) % 4) && (
                  <text x={cx} y={height - 6} fontSize="10" fill={INK_FAINT} textAnchor="middle">w{weekNumber(w)}</text>
                )}
                <rect x={cx - band / 2} y={0} width={band} height={height} fill="transparent" onPointerEnter={() => setHover(i)} onPointerDown={() => setHover(i)} />
              </g>
            );
          })}
          {totals.at(-1)! > 0 && (
            <text x={m.left + band * (weeks.length - 0.5)} y={y(totals.at(-1)!) - 5} fontSize="11" fontWeight="700" fill="#2c1a20" textAnchor="middle">{fmt(totals.at(-1)!)}</text>
          )}
        </svg>
        {hovered && (
          <Tip x={m.left + band * hover! + band / 2} y={y(totals[hover!]!)} width={width}>
            <div className="font-bold">Week {weekNumber(hovered)} · {dayDate(hovered)}</div>
            {shops.map((s) => {
              const v = value(s, hovered);
              const notes = note?.(s, hovered) ?? [];
              if (!v && !notes.length) return null;
              return (
                <div key={s} className="flex items-center gap-1.5 text-ink-soft">
                  <span className="size-2 rounded-[2px]" style={{ background: shopColor(allShops, s) }} />
                  <span className="flex-1">{s}</span>
                  <span className="font-semibold tabular-nums text-ink">{[v ? fmt(v) : "", ...notes].filter(Boolean).join(" + ")}</span>
                </div>
              );
            })}
            <div className="mt-1 border-t border-line-soft pt-1 font-semibold">Total {fmt(totals[hover!])}</div>
          </Tip>
        )}
      </div>
    </div>
  );
}
